import React, { useEffect, useMemo } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addPostReaction, deleteBoard, getBoardPermissions, removePostReaction, setBoardArchived, toggleBoardHide, toggleBoardMute } from '@/data/connect';
import { LegendList } from "@legendapp/list";
import Post from '@/components/Connect/Boards/Post';
import ContentLoader from '@/components/General/ContentLoader';
import { ConnectedReactionMenu } from '@/components/Connect/ReactionMenu';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import useMarkNotificationsRead from '@/utils/hooks/useMarkNotificationsRead';
import usePostViewTracking from '@/utils/hooks/usePostViewTracking';
import { showAlert } from '@/utils/showAlert';
import { useRouter, useNavigation, useLocalSearchParams, useIsFocused } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import PageTitle from '@/components/General/PageTitle';
import SplitView from '@/components/General/SplitView';
import BoardsList from '@/components/Connect/Boards/BoardsList';

export default function FeedScreen() {
    const isFocused = useIsFocused()

    /*** Theme & Dimensions ***/
    const router = useRouter();
    const navigation = useNavigation();
    const { boardID } = useLocalSearchParams()
    
    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: board } = Query.boards.byId({id: boardID})
    const { data: posts, isLoading: loadingPosts, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.posts.byBoardId({boardID})
    const { data: loggedInUser } = Query.people.loggedInUser()

    const flatPosts = useMemo(() => posts?.pages?.flatMap(page => page.data) || [], [posts]);

    const boardPermissions = useMemo(() => getBoardPermissions(board, loggedInUser), [board, loggedInUser]);

    const updatePostInQueryCache = (postID, updater) => {
        queryClient.setQueryData(QueryKeys.posts.byBoardId(boardID), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o.id == postID ? updater(o) : o)}))}
        })
    }

    const addReactionMutation = useMutation({
        mutationFn: addPostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions || []), {...data, Person: loggedInUser}]}))
        }
    })

    const removeReactionMutation = useMutation({
        mutationFn: removePostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions?.filter(r => !(r.postID == data.postID && r.personID == data.personID)) || [])]}))
        }
    })

    const deleteMutation = useMutation({
        mutationFn: deleteBoard,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
            router.back();
        },
    })

    const toggleBoardMuteMutation = useMutation({
        mutationFn: toggleBoardMute,
        onSuccess: (data) => {
            queryClient.setQueryData(QueryKeys.boards.byId(boardID), old => {
                if(!old || Array.isArray(old)) return old;
                return {...old, mutedForUser: data?.isMuted};
            });
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
        }
    })

    const toggleBoardHideMutation = useMutation({
        mutationFn: toggleBoardHide,
        onSuccess: (data) => {
            queryClient.setQueryData(QueryKeys.boards.byId(boardID), old => {
                if(!old || Array.isArray(old)) return old;
                return {...old, hiddenForUser: data?.isHidden, mutedForUser: data?.isMuted ?? data?.isHidden};
            });
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
        }
    })

    const archiveBoardMutation = useMutation({
        mutationFn: setBoardArchived,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.boards.byId(boardID)});
            //Drop the archived board's posts from My Feed cache immediately.
            queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()});
            //Archiving removes the board for everyone — leave its feed.
            if(variables?.isArchived) router.replace('/connect');
        }
    })

    //Board membership mirrors BoardsList.isBoardMember — hide is member-only.
    const isBoardMember = useMemo(() => {
        if(!board) return false;
        if(board.createdByID && board.createdByID === loggedInUser?.id) return true;
        if(!board.Roles || board.Roles.length === 0) return true;
        const roleIDs = new Set((loggedInUser?.Roles || loggedInUser?.roles || []).map(r => r.id || r.roleID).filter(Boolean));
        return board.Roles.some(r => roleIDs.has(r.roleID));
    }, [board, loggedInUser]);


    //Any unread notification referencing this board clears when its feed is viewed.
    //NEW_POST/NEW_COMMENT don't carry parameters.boardID — the hook falls back to path matching.
    useMarkNotificationsRead({ boardID })

    const {onViewableItemsChanged, viewabilityConfig} = usePostViewTracking();

    useEffect(() => {
        const options = []

        //Archived boards are read-dead — no new posts, no mute affordance.
        if(boardPermissions?.canPost && !board?.isArchived){
            options.push({
                name: board?.type === 'ANNOUNCEMENTS' ? 'New announcement' : 'New Post',
                icon: {android: 'add', ios: 'plus', web: 'add-outline'},
                onSelect: () => board?.type === 'ANNOUNCEMENTS' ? router.push('/connect/announcements/create') : router.push({pathname: `/connect/boards/${boardID}/posts/create`, params: {boardName: board?.name, type: "MESSAGE_BOARD"}})
            });
        }

        //Hidden boards are force-muted — no unmute path while hidden.
        if(!board?.hiddenForUser && !board?.isArchived){
            options.push({
                name: board?.type === 'ANNOUNCEMENTS' ? (board?.mutedForUser ? 'Use push first for announcements' : 'Turn off push (SMS/email fallback)') : board?.mutedForUser ? 'Unmute this Board' : 'Mute this Board',
                icon: board?.mutedForUser
                    ? {android: 'notifications', ios: 'bell', web: 'notifications-outline'}
                    : {android: 'notifications_off', ios: 'bell.slash', web: 'notifications-off-outline'},
                onSelect: () => {
                    setTimeout(() => {
                        toggleBoardMuteMutation.mutate({boardID, personID: loggedInUser?.id, isMuted: !board?.mutedForUser});
                    }, 200);
                }
            });
        }

        if(isBoardMember && board?.type !== 'ANNOUNCEMENTS'){
            options.push({
                name: board?.hiddenForUser ? 'Unhide this Board' : 'Hide this Board',
                icon: board?.hiddenForUser ? dropdownIcons.unhide : dropdownIcons.hide,
                onSelect: () => {
                    setTimeout(() => {
                        toggleBoardHideMutation.mutate({boardID, personID: loggedInUser?.id, isHidden: !board?.hiddenForUser});
                    }, 200);
                }
            });
        }

        options.push({
            name: "Board Info",
            icon: dropdownIcons.info,
            onSelect: () => router.push({pathname: `/connect/boards/${boardID}/info`})
        });

        if(board?.type === 'ANNOUNCEMENTS' && loggedInUser?.can?.('Send SMS Messages')) {
            options.push({ name: 'Announcement settings', icon: { android: 'settings', ios: 'gearshape', web: 'settings-outline' }, onSelect: () => router.push('/connect/announcements/settings') });
        }
        if(board?.id && board?.type !== 'ANNOUNCEMENTS' && (loggedInUser?.isAdmin || boardPermissions?.isAdmin)){
            options.push({name: "Edit Board", icon: {android: 'create', ios: 'pencil', web: 'create-outline'}, onSelect: () => router.push({pathname: "/connect/boards/create", params: {id: boardID, type: "MESSAGE_BOARD"}})});
            options.push({
                name: board?.isArchived ? 'Unarchive Board' : 'Archive Board',
                icon: board?.isArchived ? {android: 'unarchive', ios: 'arrow.up.bin', web: 'archive-outline'} : dropdownIcons.archive,
                onSelect: () => {
                    if(board?.isArchived){
                        archiveBoardMutation.mutate({boardID, isArchived: false});
                        return;
                    }
                    showAlert({
                        title: "Archive Board",
                        subtitle: "Archiving removes this board from everyone's view and blocks new posts, comments, and reactions. You can unarchive it later.",
                        isDestructive: true,
                        onConfirm: () => archiveBoardMutation.mutate({boardID, isArchived: true}),
                        confirmText: "Archive"
                    })
                },
                destructive: !board?.isArchived
            });
            options.push({name: "Delete Board", icon: {android: 'delete', ios: 'trash', web: 'trash-outline'}, onSelect: () => {
                showAlert({
                    title: "Delete Board",
                    subtitle: "Are you sure you want to delete this board? This will also delete all posts in the board and you will not be able to recover them.",
                    isDestructive: true,
                    onConfirm: () => deleteMutation.mutate({boardID}),
                    confirmText: "Delete"
                })
            }, destructive: true});
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={board?.name} subtitle="Message Board Feed" />,
            headerRight: options.length > 0 ? () => (
                <View style={styles.headerRight}>
                    <DropdownMenu testID="board-feed-menu" options={options}/>
                </View>
            ) : undefined,
        });

    }, [navigation, board, boardPermissions, loggedInUser, board?.mutedForUser, board?.hiddenForUser, board?.isArchived, isBoardMember]);


    const renderItem = ({ item }) => {
        return <Post key={item.id} post={item} showBoardName={false} />
    };

    const updateReaction = async (post, name) => {
        const currentUserReaction = post?.Reactions?.find(r => r.Person?.id == loggedInUser?.id)

        if(currentUserReaction?.reaction == name){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
            return
        }

        if(currentUserReaction){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
        }

        await addReactionMutation.mutateAsync({postID: post.id, reaction: name, personID: loggedInUser?.id})
    }


    let contentLoaders = [];

    if(loadingPosts){
        for(let i=0; i<5; i++){
            contentLoaders.push(<View key={"loaders" + i} style={styles.contentLoaderRow}>
                <ContentLoader containerHeight={200} borderRadius={0} staggerPosition={i}/>
            </View>)
        }
    }


    return !boardID ? <View style={styles.notFoundContainer}><Text style={styles.notFoundText}>Board not found</Text></View> : (
        <SplitView sidebar={<BoardsList selectedBoardID={boardID} display="compact" />}>
        {board?.isArchived ? (
            <View style={styles.notFoundContainer} testID="board-archived-notice">
                <Text style={styles.notFoundText}>This board has been archived</Text>
                <Text style={styles.archivedSubtext}>New posts, comments, and reactions are turned off.</Text>
            </View>
        ) : (
        <View style={styles.feedContainer}>
            <View style={styles.listWrapper}>
                <LegendList recycleItems keyExtractor={(item) => item.id}
                    contentInsetAdjustmentBehavior="automatic"
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    estimatedItemSize={220}
                    data={loadingPosts ? [] : flatPosts}
                    renderItem={renderItem}
                    onViewableItemsChanged={onViewableItemsChanged}
                    viewabilityConfig={viewabilityConfig}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={() => {
                        queryClient.invalidateQueries({queryKey: QueryKeys.boards.byId(board?.id)})
                        queryClient.invalidateQueries({queryKey: QueryKeys.posts.byBoardId(boardID)})
                    }}
                    onEndReachedThreshold={0.5}
                    onEndReached={() => {
                        if(!isFetchingNextPage && hasNextPage) fetchNextPage()
                    }}
                    ListFooterComponent={!loadingPosts
                        ? <View style={styles.footerContainer}>{
                            isFetchingNextPage 
                            ? <ContentLoader containerHeight={150} borderRadius={0} />
                            : flatPosts?.length == 0 ? <EmptyListPlaceholder icon="newspaper-outline" spacing={10} message="No posts yet!"/> 
                            : <View></View>
                        }
                        </View> 
                        : <View>
                            {contentLoaders}
                        </View>}
                    keyboardShouldPersistTaps="handled"
                />
            </View>

            <ConnectedReactionMenu
                noMenu
                focused={isFocused}
                onReactionPress={(post, name) => updateReaction(post, name)}
                canReply={false}
            />
        </View>
        )}
        </SplitView>
    )
}

const styles = StyleSheet.create(({colors, spacing}, rt) => ({
    headerRight: {
        flexDirection:"row"
    },
    contentLoaderRow: {
        flexDirection:"row",
        justifyContent:"space-between",
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 12,
        marginVertical: 8,
        overflow: 'hidden',
        borderRadius: rt.breakpoint === 'xs' ? 0 : 16,
    },
    notFoundContainer: {
        flex:1,
        alignItems:"center",
        justifyContent:"center",
        padding:20
    },
    notFoundText: {
        fontSize:20,
        fontWeight:"bold",
        color:colors.text
    },
    archivedSubtext: {
        fontSize:14,
        color:colors.textSecondary,
        marginTop:8,
        textAlign:"center"
    },
    feedContainer: {
        flex:1,
        width:"100%",
        alignItems:"center",
        backgroundColor: 'transparent',
    },
    listWrapper: {
        flex:1,
        width:"100%",
        maxWidth: 780,
    },
    listContent: {
        paddingTop: rt.breakpoint === 'xs' ? spacing.xs : spacing.sm,
    },
    footerContainer: {
        paddingBottom:80,
    }
}))